import React, { useState } from 'react';
import {
  Box, Container, Grid, Typography, Skeleton, Paper, Chip, Link,
  ToggleButton, ToggleButtonGroup, Table, TableHead, TableBody, TableRow, TableCell,
  TextField, MenuItem, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { ExpandMore } from '@mui/icons-material';
import SectionTitle from './crt/SectionTitle';
import Panel from './crt/Panel';
import { seoAPI } from '../services/api';
import UrlIndexPanel from './seo/UrlIndexPanel';
import CompetitorSerpPanel from './seo/CompetitorSerpPanel';
import TrendsCard from './seo/TrendsCard';
import ActionCenter from './seo/ActionCenter';
import BacklinksPanel from './seo/BacklinksPanel';

const fmt = (n: number | null | undefined) =>
  n == null ? '—' : Number(n).toLocaleString();

const SEVERITY_COLORS: Record<string, string> = {
  high: '#ff3344',
  med:  '#ffb000',
  low:  '#5aaa5a',
};

const KIND_LABELS: Record<string, string> = {
  missing_podcastepisode_jsonld: 'Missing PodcastEpisode JSON-LD',
  missing_yoast_title:           'Missing SEO title',
  missing_meta_description:      'Missing meta description',
  missing_focus_keyword:         'Missing focus keyword',
  missing_canonical:             'Missing canonical URL',
  missing_og_image:              'Missing Open Graph image',
  thin_content:                  'Thin content (<300 words)',
};

interface SiteTotal {
  site_slug: string;
  open: number;
  high: number;
  med: number;
  low: number;
  auto_fixable: number;
  posts_with_findings: number;
  last_run_at: string | null;
}

interface ByKind {
  site_slug: string;
  finding_kind: string;
  severity: string;
  open_findings: number;
  auto_fixable_findings: number;
}

interface AuditSummary {
  totals: SiteTotal[];
  by_kind: ByKind[];
}

interface Finding {
  site_slug: string;
  post_id: number;
  post_type: string;
  post_title: string;
  post_url: string;
  finding_kind: string;
  severity: string;
  details: string;
  auto_fixable: boolean;
  suggested_fix: string | null;
  detected_at: string;
}

const SEODashboard: React.FC = () => {
  const [siteFilter, setSiteFilter] = useState<string>('all');
  const [kindFilter, setKindFilter] = useState<string>('');
  const [severityFilter, setSeverityFilter] = useState<string>('');

  const { data: summary } = useQuery<AuditSummary>({
    queryKey: ['seo-audit-summary'],
    queryFn: seoAPI.getSummary,
    staleTime: 60_000,
  });

  const { data: findings } = useQuery({
    queryKey: ['seo-findings', siteFilter, kindFilter, severityFilter],
    queryFn: () => seoAPI.getFindings({
      site:     siteFilter !== 'all' ? siteFilter : undefined,
      kind:     kindFilter || undefined,
      severity: severityFilter || undefined,
      limit:    200,
    }),
    staleTime: 60_000,
  });

  const totals = summary?.totals || [];
  const byKind = summary?.by_kind || [];
  const filteredByKind = byKind.filter(k =>
    siteFilter === 'all' ? true : k.site_slug === siteFilter
  );

  // Aggregate kind data for the chart (sum across sites if 'all')
  const kindMap = new Map<string, { kind: string; high: number; med: number; low: number; total: number }>();
  filteredByKind.forEach(k => {
    const entry = kindMap.get(k.finding_kind) || {
      kind: k.finding_kind, high: 0, med: 0, low: 0, total: 0,
    };
    if (k.severity === 'high') entry.high += k.open_findings;
    if (k.severity === 'med')  entry.med  += k.open_findings;
    if (k.severity === 'low')  entry.low  += k.open_findings;
    entry.total += k.open_findings;
    kindMap.set(k.finding_kind, entry);
  });
  const kindChartData = Array.from(kindMap.values()).sort((a, b) => b.total - a.total);

  const totalFindings = totals.reduce((a, t) => a + t.open, 0);
  const totalHigh     = totals.reduce((a, t) => a + t.high, 0);
  const totalAutoFix  = totals.reduce((a, t) => a + t.auto_fixable, 0);
  const totalPosts    = totals.reduce((a, t) => a + t.posts_with_findings, 0);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box mb={4}>
        <SectionTitle sub="&gt; what to do today + how things are trending">
          SEO :: ACTION CONSOLE
        </SectionTitle>
      </Box>

      {/* TRENDS — "did things get better or worse?" answered in 5 seconds. */}
      <Box sx={{ mb: 4 }}>
        <TrendsCard />
      </Box>

      {/* ACTION CENTER — what to do today, surfaced from GSC. */}
      <Box sx={{ mb: 4 }}>
        <ActionCenter />
      </Box>

      {/* Competitor SERP rankings — weekly Serper.dev snapshot */}
      <Box sx={{ mb: 4 }}>
        <CompetitorSerpPanel />
      </Box>

      {/* Indexability — how Google sees us */}
      <Box sx={{ mb: 4 }}>
        <UrlIndexPanel />
      </Box>

      {/* Backlinks — Bing WMT (GSC has no link API) */}
      <Box sx={{ mb: 4 }}>
        <BacklinksPanel />
      </Box>

      {/* On-page audit health — secondary; collapsed by default */}
      <Box sx={{ mb: 4 }}>
        <Accordion variant="outlined" defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">
              On-page audit health — {fmt(totalFindings)} open findings
              {totalHigh > 0 && (
                <Chip label={`${totalHigh} high`} size="small" color="error" sx={{ ml: 1 }} />
              )}
              {totalAutoFix > 0 && (
                <Chip label={`${totalAutoFix} auto-fixable`} size="small" color="info" sx={{ ml: 1 }} />
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {/* KPI tiles */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 6, md: 3 }}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">OPEN FINDINGS</Typography>
                  <Typography variant="h5">{fmt(totalFindings)}</Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">HIGH SEVERITY</Typography>
                  <Typography variant="h5" sx={{ color: 'error.main' }}>{fmt(totalHigh)}</Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">AUTO-FIXABLE</Typography>
                  <Typography variant="h5" sx={{ color: 'info.main' }}>{fmt(totalAutoFix)}</Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">POSTS AFFECTED</Typography>
                  <Typography variant="h5">{fmt(totalPosts)}</Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Per-site totals */}
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 3 }}>
              {totals.map((t) => (
                <Box key={t.site_slug} sx={{ minWidth: 240 }}>
                  <Typography variant="overline" color="text.secondary">
                    {t.site_slug.toUpperCase()}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                    <Chip label={`${t.high} high`} size="small" color="error" variant="outlined" />
                    <Chip label={`${t.med} med`}  size="small" color="warning" variant="outlined" />
                    <Chip label={`${t.low} low`}  size="small" color="success" variant="outlined" />
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    {t.posts_with_findings} posts · {t.auto_fixable} auto-fixable
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled' }}>
                    last scan: {t.last_run_at ? new Date(t.last_run_at).toLocaleString() : '—'}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Filters */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">filter:</Typography>
              <ToggleButtonGroup
                value={siteFilter}
                exclusive
                onChange={(_, v) => v && setSiteFilter(v)}
                size="small"
              >
                <ToggleButton value="all">all sites</ToggleButton>
                <ToggleButton value="spoutlore">Spout Lore</ToggleButton>
                <ToggleButton value="mallbrats">Mall Brats</ToggleButton>
              </ToggleButtonGroup>

              <TextField
                select size="small" label="severity"
                value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="">any</MenuItem>
                <MenuItem value="high">high</MenuItem>
                <MenuItem value="med">med</MenuItem>
                <MenuItem value="low">low</MenuItem>
              </TextField>

              <TextField
                select size="small" label="finding kind"
                value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}
                sx={{ minWidth: 280 }}
              >
                <MenuItem value="">any</MenuItem>
                {Object.entries(KIND_LABELS).map(([k, label]) => (
                  <MenuItem key={k} value={k}>{label}</MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Findings-by-kind chart */}
            <Box sx={{ mb: 3 }}>
              <Panel label="FINDINGS BY KIND">
                {kindChartData.length === 0 ? (
                  <Skeleton variant="rectangular" height={300} />
                ) : (
                  <ResponsiveContainer width="100%" height={Math.max(280, kindChartData.length * 44)}>
                    <BarChart data={kindChartData} layout="vertical" margin={{ left: 220, right: 24 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e3a1e" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis
                        type="category" dataKey="kind"
                        tick={{ fontSize: 11 }}
                        tickFormatter={(k) => KIND_LABELS[k] || k}
                        width={220}
                      />
                      <Tooltip formatter={(v: number) => fmt(v)} />
                      <Legend />
                      <Bar dataKey="high" stackId="s" fill={SEVERITY_COLORS.high} name="high" />
                      <Bar dataKey="med"  stackId="s" fill={SEVERITY_COLORS.med}  name="med" />
                      <Bar dataKey="low"  stackId="s" fill={SEVERITY_COLORS.low}  name="low" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Panel>
            </Box>

            {/* Findings table */}
            <Box>
              <Panel label={`AUDIT FINDINGS (${findings?.total || 0} total)`}>
                {!findings ? (
                  <Skeleton variant="rectangular" height={400} />
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Site</TableCell>
                          <TableCell>Post</TableCell>
                          <TableCell>Kind</TableCell>
                          <TableCell>Severity</TableCell>
                          <TableCell>Details</TableCell>
                          <TableCell>Fix</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {findings.findings.slice(0, 200).map((f: Finding) => (
                          <TableRow key={`${f.site_slug}-${f.post_id}-${f.finding_kind}`} hover>
                            <TableCell>
                              <Chip label={f.site_slug} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 320 }}>
                              <Link href={f.post_url} target="_blank" rel="noopener noreferrer">
                                {f.post_title || '(no title)'}
                              </Link>
                              <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                                {f.post_type} #{f.post_id}
                              </Typography>
                            </TableCell>
                            <TableCell>{KIND_LABELS[f.finding_kind] || f.finding_kind}</TableCell>
                            <TableCell>
                              <Chip
                                label={f.severity}
                                size="small"
                                color={f.severity === 'high' ? 'error' : f.severity === 'med' ? 'warning' : 'success'}
                              />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 280 }}>{f.details}</TableCell>
                            <TableCell sx={{ maxWidth: 280 }}>
                              {f.auto_fixable ? (
                                <Typography variant="caption" sx={{ color: 'info.main' }}>
                                  ⚡ auto: {f.suggested_fix}
                                </Typography>
                              ) : (
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  manual: {f.suggested_fix || '—'}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {findings.total > 200 && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                        showing first 200 of {findings.total} — apply more filters to drill in
                      </Typography>
                    )}
                  </Box>
                )}
              </Panel>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Container>
  );
};

export default SEODashboard;
